import React, {useEffect} from 'react'
import styled from 'styled-components'
import Arrow from '@material-ui/icons/ArrowForward'
import {EnabledSwitch} from 'lib/ui/form/Switch'
import {spacing} from 'lib/ui/theme'
import {Widget, RedirectTemplate} from 'Event/Widget/template'
import {onChangeCheckedHandler} from 'lib/dom'
import TextField, {NumberField} from 'lib/ui/TextField'
import CopyButton from 'organization/Event/WidgetConfig/CopyButton'
import {renderRedirect} from 'Event/Widget/script/elements/redirect'
import {useWidgetCode} from 'organization/Event/WidgetConfig/generate-widget-code'

export default function RedirectWidget(props: {
  redirect: RedirectTemplate
  onChange: (updatedFields: Partial<RedirectTemplate>) => void
  widget: Widget
}) {
  const {redirect, onChange, widget} = props

  const setActive = (isActive: boolean) => {
    onChange({enabled: isActive})
  }

  return (
    <WidgetHolder>
      <WidgetHeader>
        <HeaderLeft>
          <HeaderIcon>
            <Arrow />
          </HeaderIcon>
          <HeaderText>
            <Title isActive={redirect.enabled}>Redirect to Dashboard</Title>
            <Description isActive={redirect.enabled}>
              Automatically redirects attendees to your event dashboard
            </Description>
          </HeaderText>
        </HeaderLeft>
        <HeaderRight>
          <WidgetEnabledSwitch
            checked={redirect.enabled}
            onChange={onChangeCheckedHandler(setActive)}
            aria-label="toggle visible widget redirect"
          />
        </HeaderRight>
      </WidgetHeader>

      <Config redirect={redirect} onChange={onChange} widget={widget} />
    </WidgetHolder>
  )
}

function Config(props: {
  redirect: RedirectTemplate
  onChange: (updatedFields: Partial<RedirectTemplate>) => void
  widget: Widget
}) {
  const {redirect, onChange, widget} = props

  const handleLabelChange = (value: string) => {
    onChange({text: value})
  }

  const handleDelayChange = (value: number) => {
    onChange({delay: value})
  }

  const {widgetCode, regenerate} = useWidgetCode('redirect', widget)

  const ref = React.useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (ref.current) {
      renderRedirect(ref.current, widget, '')
    }
  }, [widget, ref])

  if (!redirect.enabled) {
    return null
  }

  return (
    <>
      <Preview>
        <TextField
          label="Loading text"
          name="title"
          value={redirect.text}
          fullWidth
          variant="outlined"
          onChange={handleLabelChange}
        />

        <NumberField
          label="Redirect delay (seconds)"
          value={redirect.delay || 0}
          fullWidth
          variant="outlined"
          onChange={handleDelayChange}
        />

        <SubTitle>Preview</SubTitle>

        <PreviewHolder>
          <div ref={ref}></div>
        </PreviewHolder>
      </Preview>
      <WidgetContainer>
        <WidgetTitle>
          <SubTitle>Widget Code</SubTitle>
          <CopyButton value={widgetCode} onCopy={regenerate} />
        </WidgetTitle>
        <CodeHolder>
          <code>{widgetCode}</code>
        </CodeHolder>
      </WidgetContainer>
    </>
  )
}

export const WidgetHolder = styled.div`
  border: 1px solid #dcdcdc;
  padding: 25px;
  border-radius: 4px;
  margin-top: ${spacing[8]};
`

export const WidgetHeader = styled.div`
  display: flex;
  align-items: start;
  gap: 25px;
`

export const HeaderIcon = styled.div`
  background: #eaeaea;
  padding: 8px;
  display: flex;
  border-radius: 4px;
  color: #3490dc;
`

export const HeaderText = styled.div``

export const HeaderLeft = styled.div`
  display: flex;
  align-items: start;
  gap: 15px;
  flex: 1;
`

export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
`

export const Title = styled.h3<{isActive: boolean}>`
  margin: 0 0 8px 0;
  color: ${(props) => (props.isActive ? 'inherit' : '#757675')};
`

export const Description = styled.p<{isActive: boolean}>`
  margin: 0;
  color: ${(props) => (props.isActive ? 'inherit' : '#757675')};
`

export const WidgetEnabledSwitch = styled(EnabledSwitch)`
  margin-bottom: 0;
`

export const PreviewHolder = styled.div`
  border-radius: 4px;
  padding: ${spacing[12]};
  background-color: #ffffff;
  text-align: center;
  border: 1px dashed;
`

export const Preview = styled.div`
  margin-top: ${spacing[8]};
`

export const WidgetContainer = styled.div`
  margin-top: ${spacing[8]};
`

export const CodeHolder = styled.pre`
  background-color: #2d2d2d;
  color: #f8f8f2;
  padding: 16px;
  border-radius: 4px;
  overflow-x: auto;
`

export const SubTitle = styled.h4`
  margin-bottom: ${spacing[4]};

  > i {
    font-weight: normal;
  }
`

export const WidgetTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`
